import { useEffect, useState, useMemo } from "react";
import { CheckCircle2, Circle, MapPin, ChevronRight, X, SkipForward } from "lucide-react";
import api from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";

/**
 * PatientRoadmap — compact, read-only recovery roadmap shown above the
 * Day / Week / Month view controls on My Plan. The patient can inspect any
 * phase; only the clinician can advance, confirm criteria or change phases.
 *
 * Design rules (iter112):
 *  · Distinct visual state for Completed (emerald), Current (#f47a20)
 *    and Upcoming (subdued zinc) — never colour-only.
 *  · The phase position ("Phase 2 of 6") indicates location in the
 *    roadmap, not a recovery percentage.
 *  · Empty state is calm: "Your clinician has not assigned a roadmap yet."
 *  · Load failure shows a retry button rather than the empty state.
 */
export default function PatientRoadmap({ roadmap: roadmapProp, history: historyProp }) {
  // Controlled mode (Batch 8B Recovery Plan): when a roadmap prop is supplied,
  // render from props and skip the self-fetch. Uncontrolled: fetch /roadmaps/mine.
  const controlled = roadmapProp !== undefined;
  const [state, setState] = useState({
    loading: !controlled, error: null,
    roadmap: controlled ? (roadmapProp || null) : null,
    history: controlled ? (historyProp || []) : [],
  });
  const [openPhaseId, setOpenPhaseId] = useState(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get("/roadmaps/mine");
      setState({
        loading: false, error: null,
        roadmap: data?.roadmap || null,
        history: data?.history || [],
      });
    } catch (e) {
      setState({
        loading: false,
        error: e?.response?.data?.detail || "Could not load your roadmap.",
        roadmap: null, history: [],
      });
    }
  };

  useEffect(() => {
    if (controlled) {
      setState({ loading: false, error: null, roadmap: roadmapProp || null, history: historyProp || [] });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled, roadmapProp, historyProp]);

  // Hooks MUST run in the same order on every render — compute `reachedIds`
  // before any early return (loading / error / empty state).
  const reachedIds = useMemo(() => {
    const s = new Set();
    (state.history || []).forEach((h) => { if (h.to_phase_id) s.add(h.to_phase_id); });
    return s;
  }, [state.history]);

  if (state.loading) {
    return (
      <div className="fl-card fl-card-elevated p-4 mb-4" data-testid="patient-roadmap-loading">
        <Skeleton className="h-4 w-40 rounded" />
        <Skeleton className="h-3 w-full mt-3 rounded" />
        <Skeleton className="h-3 w-2/3 mt-2 rounded" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="fl-card p-4 mb-4 border" style={{ borderColor: "rgba(239,68,68,0.35)" }}
           data-testid="patient-roadmap-error">
        <div className="fl-label text-red-300">Couldn't load your roadmap</div>
        <p className="text-sm text-zinc-400 mt-1">{state.error}</p>
        <button onClick={load} className="fl-btn text-xs mt-3" data-testid="patient-roadmap-retry">Retry</button>
      </div>
    );
  }

  if (!state.roadmap) {
    return (
      <div className="fl-card p-4 mb-4" data-testid="patient-roadmap-empty">
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-[#f47a20]" />
          <div className="fl-label text-xs">Your recovery roadmap</div>
        </div>
        <p className="text-sm text-zinc-400 mt-2">
          Your clinician has not assigned a roadmap yet.
        </p>
      </div>
    );
  }

  const { title, phases, current_phase_id } = state.roadmap;
  const sorted = [...phases].sort((a, b) => (a.order || 0) - (b.order || 0));
  const currentIdx = sorted.findIndex((p) => p.id === current_phase_id);
  const currentPhase = currentIdx >= 0 ? sorted[currentIdx] : null;

  const statusOf = (idx) => {
    if (currentIdx < 0) return "upcoming";
    if (idx === currentIdx) return "current";
    if (idx > currentIdx) return "upcoming";
    return reachedIds.has(sorted[idx].id) ? "completed" : "skipped";
  };

  const openPhase = openPhaseId ? sorted.find((p) => p.id === openPhaseId) : null;

  return (
    <div className="fl-card fl-card-elevated p-4 sm:p-5 mb-4" data-testid="patient-roadmap">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-[#f47a20]" />
            <div className="fl-label text-xs">Your recovery roadmap</div>
          </div>
          <h3 className="fl-heading text-lg sm:text-xl mt-1 truncate" data-testid="patient-roadmap-title">
            {title || "Recovery roadmap"}
          </h3>
          {currentPhase && (
            <div className="text-xs text-zinc-400 mt-1" data-testid="patient-roadmap-position">
              Phase {currentIdx + 1} of {sorted.length} · <span className="text-[#f47a20]">{currentPhase.title}</span>
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <ol
        className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-1 sm:items-stretch"
        aria-label="Recovery roadmap phases"
        data-testid="patient-roadmap-timeline"
      >
        {sorted.map((p, i) => {
          const st = statusOf(i);
          const color =
            st === "completed" ? "emerald-400" :
            st === "current" ? "[#f47a20]" :
            st === "skipped" ? "amber-400" : "zinc-500";
          const border =
            st === "completed" ? "border-emerald-500/40" :
            st === "current" ? "border-[#f47a20]/60" :
            st === "skipped" ? "border-amber-500/40 border-dashed" : "border-white/10";
          const bg =
            st === "current" ? "bg-[#f47a20]/10" : "bg-transparent";
          const StatusIcon =
            st === "completed" ? CheckCircle2 :
            st === "current" ? MapPin :
            st === "skipped" ? SkipForward : Circle;

          return (
            <li key={p.id} className="flex-1 min-w-0" data-testid={`patient-roadmap-phase-${i}`}>
              <button
                type="button"
                onClick={() => setOpenPhaseId(p.id)}
                aria-label={`${p.title} — ${st}`}
                data-testid={`patient-roadmap-phase-btn-${i}`}
                className={`w-full h-full text-left rounded-md border ${border} ${bg} p-3 hover:border-[#f47a20]/60 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f47a20]/60`}
              >
                <div className="flex items-center gap-2">
                  <StatusIcon size={14} className={`text-${color}`} />
                  <span className={`text-[10px] uppercase tracking-widest ${
                    st === "completed" ? "text-emerald-400" :
                    st === "current" ? "text-[#f47a20]" :
                    st === "skipped" ? "text-amber-400" : "text-zinc-500"
                  }`}>
                    {st === "completed" ? "Completed" :
                     st === "current" ? "Current" :
                     st === "skipped" ? "Skipped" : "Upcoming"}
                  </span>
                </div>
                <div className={`fl-heading text-sm mt-1 truncate ${
                  st === "upcoming" ? "text-zinc-300" : "text-white"
                }`}>{p.title}</div>
                <div className="text-[10px] text-zinc-500 mt-1">Phase {i + 1} of {sorted.length}</div>
              </button>
            </li>
          );
        })}
      </ol>

      <p className="text-[11px] text-zinc-500 mt-3">
        Phase progression is reviewed and approved by your clinician. The number shows your
        position in the roadmap, not a recovery percentage.
      </p>

      {openPhase && (
        <PhaseDetailModal
          phase={openPhase}
          isCurrent={openPhase.id === current_phase_id}
          onClose={() => setOpenPhaseId(null)}
        />
      )}
    </div>
  );
}

function PhaseDetailModal({ phase, isCurrent, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start sm:items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="patient-roadmap-phase-modal"
    >
      <div
        role="dialog" aria-modal="true" aria-label={`${phase.title} phase details`}
        onClick={(e) => e.stopPropagation()}
        className="fl-card-elevated w-full max-w-lg overflow-hidden max-h-[85vh] flex flex-col"
        style={{ background: "var(--fl-bg-2)" }}
      >
        <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b" style={{ borderColor: "var(--fl-border)" }}>
          <div className="min-w-0">
            <div className="fl-label text-[#f47a20]">
              {isCurrent ? "Current phase" : "Phase details"}
            </div>
            <div className="fl-heading text-xl mt-1 truncate">{phase.title}</div>
          </div>
          <button onClick={onClose} aria-label="Close phase details"
                  className="text-zinc-400 hover:text-white -mr-1"
                  data-testid="patient-roadmap-phase-modal-close">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto text-sm">
          {phase.description && (
            <p className="text-zinc-300 leading-relaxed">{phase.description}</p>
          )}

          <div>
            <div className="fl-label mb-1">Goals</div>
            {phase.goals && phase.goals.length > 0 ? (
              <ul className="list-disc list-inside space-y-1 text-zinc-300">
                {phase.goals.map((g, i) => <li key={i}>{g}</li>)}
              </ul>
            ) : (
              <p className="text-zinc-500 text-xs">No goals recorded for this phase yet.</p>
            )}
          </div>

          <div>
            <div className="fl-label mb-1">Progression criteria</div>
            {phase.criteria && phase.criteria.length > 0 ? (
              <ul className="space-y-1.5">
                {phase.criteria.map((c) => (
                  <li key={c.id} className="flex items-start gap-2" data-testid={`patient-criterion-${c.id}`}>
                    {c.confirmed
                      ? <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" />
                      : <Circle size={14} className="text-zinc-500 mt-0.5 shrink-0" />}
                    <div className="flex-1">
                      <div className="text-zinc-200">{c.label}</div>
                      <div className={`text-[11px] ${c.confirmed ? "text-emerald-400" : "text-zinc-500"}`}>
                        {c.confirmed ? "Confirmed by your clinician" : "Not yet confirmed"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-zinc-500 text-xs">No criteria recorded for this phase yet.</p>
            )}
          </div>

          <p className="text-[11px] text-zinc-500">
            Your clinician reviews and confirms progression. Selecting a phase here does not
            change your current phase.
          </p>
        </div>
      </div>
    </div>
  );
}
